import { LK_PERMISSIONS as LK } from './legalKnowledgePermissions';
import { MD_PERMISSIONS as MD } from './metadataPermissions';
import type { AccessRequirement } from './types';

/**
 * Route-level access policy (derived from the provided "Roles & Permissions" PDF).
 * Rule: allow when (role matches OR permission matches).
 */
export const ROUTE_ACCESS: Record<string, AccessRequirement | undefined> = {
  // Work
  '/tasks': { anyPermissions: ['VIEW_TASKS_CASES'], denyAllocationOfficer: true, denyManagementAssistant: true },
  '/tasks/:taskId': {
    anyPermissions: [
      'VIEW_TASKS_CASES',
      'ALLOCATION_OFFICER_VIEW',
      'ASSIGNED_OFFICER_VIEW',
      'STATE_ATTORNEY_OFFICER_VIEW',
      'VIEW_ESCALATION_TASKS',
    ],
    anyRoleCodes: [
      'ALLOCATION_OFFICER',
      'COUNSEL',
      'ATTORNEY_GENERAL',
      'SOLICITOR_GENERAL',
      'JUNIOR_COUNSEL',
      'ASSIGNED_OFFICER',
      'STATE_ATTORNEY',
    ],
  },
  '/tasks/assign': { anyPermissions: ['ALLOCATION_OFFICER_VIEW'], anyRoleCodes: ['ALLOCATION_OFFICER'] },
  '/tasks/case-details': { anyRoleCodes: ['OFFICE_CLERK'] },
  '/tasks/escalation': { anyPermissions: ['VIEW_ESCALATION_TASKS'] },
  '/tasks/attending': { anyPermissions: ['STATE_ATTORNEY_OFFICER_VIEW'] },
  '/tasks/create-case-request': { anyPermissions: ['CREATE_CASE_REQUEST'] },
  '/requests/new': { anyPermissions: ['CREATE_CASE_REQUEST'] },
  '/requests': { anyPermissions: ['CREATE_CASE_REQUEST', 'VIEW_TASKS_CASES'] },

  // Notifications / Profile / Case Scenarios (header Info)
  '/notifications': { anyPermissions: ['VIEW_NOTIFICATIONS'] },
  '/case-scenarios': undefined,
  '/case-scenarios/pdf': undefined,
  '/profile': { anyPermissions: ['VIEW_PROFILE'] },

  // Operations
  '/mail-room/requests': { anyPermissions: ['MAILROOM_OFFICER_VIEW'], anyRoleCodes: ['MAIL_OPERATIONS_OFFICER'] },
  '/record-room/completed-files': { anyPermissions: ['REC_MANAGEMENT_OFFICER_VIEW'], anyRoleCodes: ['RECORD_MANAGEMENT_OFFICER'] },
  '/record-room/qr-scanning-log': { anyPermissions: ['REC_MANAGEMENT_OFFICER_VIEW'], anyRoleCodes: ['RECORD_MANAGEMENT_OFFICER'] },
  '/record-room/qr-scanning-logs': { anyPermissions: ['REC_MANAGEMENT_OFFICER_VIEW'], anyRoleCodes: ['RECORD_MANAGEMENT_OFFICER'] },
  '/legal-knowledge/archive': { anyPermissions: [LK.VIEW_LEGAL_ARCHIVE] },
  '/legal-knowledge/archive/:id': { anyPermissions: [LK.VIEW_LEGAL_ARCHIVE, LK.VIEW_ARCHIVE_CASES] },
  '/legal-knowledge/archive/management/outcomes': { anyPermissions: [LK.VIEW_EXTRACTED_CASE_OUTCOMES] },
  '/legal-knowledge/archive/management/outcomes/:id': { anyPermissions: [LK.VIEW_EXTRACTED_CASE_OUTCOMES] },
  '/legal-knowledge/archive/management/dashboard': { anyPermissions: [LK.VIEW_EXTRACTED_CASE_OUTCOMES] },

  // Admin
  '/admin/intranet-users': { anyPermissions: ['INTRANET_VIEW_USER'] },
  '/admin/intranet-users/new': { anyPermissions: ['INTRANET_CREATE_USER'] },
  '/admin/intranet-users/:userId/edit': { anyPermissions: ['INTRANET_UPDATE_USER'] },
  '/admin/user-categories': { anyPermissions: ['INTRANET_VIEW_USER'] },
  '/admin/user-categories/new': { anyPermissions: ['INTRANET_CREATE_USER'] },
  '/admin/user-categories/:categoryAllocationId/edit': { anyPermissions: ['INTRANET_UPDATE_USER'] },
  '/admin/extranet-users': { anyPermissions: ['EXTRANET_VIEW_USER'] },
  '/admin/extranet-users/new': { anyPermissions: ['EXTRANET_CREATE_USER'] },
  '/admin/extranet-users/:userId/edit': { anyPermissions: ['EXTRANET_UPDATE_USER'] },
  '/admin/roles-permissions': { anyPermissions: ['VIEW_ROLES_PERMISSIONS'] },
  '/admin/roles-permissions/new': { anyPermissions: ['CREATE_ROLE'] },
  '/admin/roles-permissions/:roleId/edit': { anyPermissions: ['UPDATE_ROLE'] },
  '/admin/roles-permissions/:roleId/view': { anyPermissions: ['VIEW_ROLES_PERMISSIONS'] },

  // Dashboards / Audit / Reports / Workflow
  '/dashboards': { anyPermissions: ['VIEW_DASHBOARD'] },
  '/dashboards/sg': { anyPermissions: ['VIEW_SG_DASHBOARD'] },
  '/dashboards/operational': { anyPermissions: ['VIEW_DASHBOARD'] },
  '/audit-logs': { anyPermissions: ['VIEW_AUDIT_LOGS'] },
  '/reports/user-performance': { anyPermissions: ['VIEW_DASHBOARD'] },
  '/reports/case-tracking': { anyPermissions: ['VIEW_DASHBOARD'] },
  '/reports/workflow-performance': { anyPermissions: ['VIEW_DASHBOARD'] },
  '/workflow/templates': { anyPermissions: ['VIEW_WORKFLOW_TEMPLATE', 'MANAGE_WORKFLOW'] },
  '/workflow/templates/designer': { anyPermissions: ['VIEW_WORKFLOW_TEMPLATE', 'CREATE_WORKFLOW_TEMPLATE'] },
  '/workflow/kpi-configuration': { anyPermissions: ['MANAGE_WORKFLOW'] },
  '/workflow/escalation-rules': { anyPermissions: ['MANAGE_WORKFLOW'] },

  // Metadata

  '/metadata/courts': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/courts/new': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/courts/:courtId/edit': { anyPermissions: [MD.MANAGE_MASTER_DATA] },

  '/metadata/languages': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/languages/new': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/languages/:languageId/edit': { anyPermissions: [MD.MANAGE_MASTER_DATA] },

  '/metadata/subcategories': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/subcategories/new': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/subcategories/:subCategoryId/edit': { anyPermissions: [MD.MANAGE_MASTER_DATA] },

  '/metadata/countries': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/countries/new': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/countries/:countryId/edit': { anyPermissions: [MD.MANAGE_MASTER_DATA] },

  '/metadata/nationalities': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/nationalities/new': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/nationalities/:nationalityId/edit': { anyPermissions: [MD.MANAGE_MASTER_DATA] },

  '/metadata/police-stations': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/police-stations/new': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/police-stations/:policeStationId/edit': { anyPermissions: [MD.MANAGE_MASTER_DATA] },

  '/metadata/letter-templates': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/letter-templates/:letterTemplateId/edit': { anyPermissions: [MD.MANAGE_MASTER_DATA] },

  '/metadata/subsidiary-tasks': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/subsidiary-tasks/new': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/subsidiary-tasks/:subsidiaryTaskId/edit': { anyPermissions: [MD.MANAGE_MASTER_DATA] },

  '/metadata/case-scenarios': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/case-scenarios/new': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
  '/metadata/case-scenarios/:caseScenarioId/edit': { anyPermissions: [MD.MANAGE_MASTER_DATA] },
};

